import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { baseQueryWithAuth } from '../auth';
import { APIVersion } from '../../constants/keys';
import type { CurrentUserApi, UserApi } from '../../types/user';

const transformErrorResponse = (response: FetchBaseQueryError) =>
  (response as { data?: { errors?: unknown } }).data?.errors;

interface GetUserArgs {
  userId: string;
}

interface UpdateUserArgs {
  userId: string;
  avatar?: File;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  position?: string;
  old_password?: string;
  new_password?: string;
}

interface DeleteUserArgs {
  userId: string;
}

// User
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getCurrentUser: builder.query<CurrentUserApi, void>({
      query: () => ({
        url: `/core-api/${APIVersion}/current-user`,
      }),
    }),
    getUser: builder.query<UserApi, GetUserArgs>({
      query: ({ userId }) => ({
        url: `/iam-api/${APIVersion}/users/${userId}`,
      }),
    }),
    updateUser: builder.mutation<UserApi, UpdateUserArgs>({
      query: ({ userId, ...fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value as string | Blob);
          }
        });
        return {
          url: `/iam-api/${APIVersion}/users/${userId}`,
          method: 'PATCH',
          body: formData,
        };
      },
      transformErrorResponse,
    }),
    deleteUser: builder.mutation<void, DeleteUserArgs>({
      query: ({ userId }: DeleteUserArgs) => ({
        url: `/iam-api/${APIVersion}/users/${userId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = userApi;
